import { create } from 'zustand';
import {
  User,
  Subscription,
  SerialActivationResponse,
  OtpRequestPayload,
  OtpRequestResponse,
  OtpVerifyResponse,
} from '../types';

interface AuthState {
  user: User | null;
  subscriptions: Subscription[];
  isLoading: boolean;
  error: string | null;
  otpPhoneNumber: string | null;
  otpExpiresAt: string | null;
  
  // Actions
  fetchUserProfile: () => Promise<void>;
  updateUserProfile: (data: Partial<User>) => Promise<void>;
  activateSerial: (serialCode: string) => Promise<boolean>;
  requestOtp: (payload: OtpRequestPayload) => Promise<boolean>;
  verifyOtp: (phoneNumber: string, code: string) => Promise<boolean>;
  clearOtpState: () => void;
  setOtpState: (phoneNumber: string | null, expiresAt: string | null) => void;
  logout: () => void;
  clearError: () => void;
  updateSubscriptionsTimer: () => void;
  initializeFromStorage: () => void;
}

const API_BASE = 'https://course-platform-api.fedacreativity.workers.dev';
const USER_STORAGE_KEY = 'course_platform_user';
const SUBSCRIPTION_STORAGE_KEY = 'course_platform_subscriptions';
const OTP_STORAGE_KEY = 'course_platform_otp';

const calculateDaysRemaining = (endDate: string): number => {
  const now = new Date();
  const end = new Date(endDate);
  const diffTime = end.getTime() - now.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return Math.max(0, diffDays);
};

const updateSubscriptionsInRealTime = (subscriptions: Subscription[]): Subscription[] => {
  return subscriptions.map((sub) => ({
    ...sub,
    days_remaining: calculateDaysRemaining(sub.end_date),
    is_valid: calculateDaysRemaining(sub.end_date) > 0,
  }));
};

const persistAuthToStorage = (
  user: User | null,
  subscriptions: Subscription[],
  otpPhoneNumber: string | null,
  otpExpiresAt: string | null,
) => {
  try {
    if (user) {
      localStorage.setItem(USER_STORAGE_KEY, JSON.stringify(user));
    } else {
      localStorage.removeItem(USER_STORAGE_KEY);
    }

    if (subscriptions.length > 0) {
      localStorage.setItem(SUBSCRIPTION_STORAGE_KEY, JSON.stringify(subscriptions));
    } else {
      localStorage.removeItem(SUBSCRIPTION_STORAGE_KEY);
    }

    if (otpPhoneNumber && otpExpiresAt) {
      localStorage.setItem(
        OTP_STORAGE_KEY,
        JSON.stringify({ phone_number: otpPhoneNumber, expires_at: otpExpiresAt }),
      );
    } else {
      localStorage.removeItem(OTP_STORAGE_KEY);
    }
  } catch (error) {
    console.error('خطأ في حفظ بيانات المستخدم في التخزين المحلي:', error);
  }
};

export const useAuthStore = create<AuthState>((set, get) => ({
  user: null,
  subscriptions: [],
  isLoading: false,
  error: null,
  otpPhoneNumber: null,
  otpExpiresAt: null,

  fetchUserProfile: async () => {
    set({ isLoading: true, error: null });

    try {
      const response = await fetch(`${API_BASE}/api/user/profile`);

      if (!response.ok) {
        throw new Error('فشل في جلب بيانات المستخدم');
      }

      const data = await response.json();
      const normalizedSubscriptions: Subscription[] = updateSubscriptionsInRealTime(
        data.subscriptions || [],
      );
      const normalizedUser: User | null = data.user || null;

      persistAuthToStorage(normalizedUser, normalizedSubscriptions, get().otpPhoneNumber, get().otpExpiresAt);

      set({
        user: normalizedUser,
        subscriptions: normalizedSubscriptions,
        isLoading: false,
      });
    } catch (error) {
      try {
        const savedUser = localStorage.getItem(USER_STORAGE_KEY);
        const savedSubscriptions = localStorage.getItem(SUBSCRIPTION_STORAGE_KEY);

        if (savedUser && savedSubscriptions) {
          const parsedSubscriptions: Subscription[] = JSON.parse(savedSubscriptions);
          const parsedUser: User = JSON.parse(savedUser);

          let restoredOtpPhone: string | null = null;
          let restoredOtpExpiry: string | null = null;
          const savedOtp = localStorage.getItem(OTP_STORAGE_KEY);

          if (savedOtp) {
            try {
              const parsedOtp = JSON.parse(savedOtp);
              if (parsedOtp?.expires_at && new Date(parsedOtp.expires_at).getTime() > Date.now()) {
                restoredOtpPhone = parsedOtp.phone_number ?? null;
                restoredOtpExpiry = new Date(parsedOtp.expires_at).toISOString();
              } else {
                localStorage.removeItem(OTP_STORAGE_KEY);
              }
            } catch (parseError) {
              localStorage.removeItem(OTP_STORAGE_KEY);
            }
          }

          set({
            user: parsedUser,
            subscriptions: updateSubscriptionsInRealTime(parsedSubscriptions),
            otpPhoneNumber: restoredOtpPhone,
            otpExpiresAt: restoredOtpExpiry,
            isLoading: false,
            error: 'تم استرداد البيانات من التخزين المحلي',
          });
          return;
        }
      } catch (storageError) {
        console.error('خطأ في استرداد البيانات من localStorage:', storageError);
      }

      set({ error: error instanceof Error ? error.message : 'حدث خطأ غير متوقع', isLoading: false });
    }
  },

  updateUserProfile: async (userData: Partial<User>) => {
    set({ isLoading: true, error: null });

    try {
      const response = await fetch(`${API_BASE}/api/user/profile`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(userData),
      });

      if (!response.ok) {
        throw new Error('فشل في تحديث البيانات');
      }

      await get().fetchUserProfile();
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'حدث خطأ غير متوقع', isLoading: false });
    }
  },

  activateSerial: async (serialCode: string): Promise<boolean> => {
    set({ isLoading: true, error: null });

    try {
      const response = await fetch(`${API_BASE}/api/user/activate-serial`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ serial_code: serialCode }),
      });

      const data: SerialActivationResponse = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.message || 'فشل في تفعيل السيريال نمبر');
      }

      await get().fetchUserProfile();
      return true;
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'حدث خطأ غير متوقع', isLoading: false });
      return false;
    }
  },

  requestOtp: async (payload: OtpRequestPayload): Promise<boolean> => {
    set({ isLoading: true, error: null });

    try {
      const response = await fetch(`${API_BASE}/api/auth/otp/request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      const data: OtpRequestResponse = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.message || 'تعذر إرسال رمز التحقق');
      }

      let expiresAtIso: string | null = null;
      if (data.expires_at) {
        expiresAtIso = new Date(data.expires_at).toISOString();
      } else if (typeof data.expires_in === 'number') {
        expiresAtIso = new Date(Date.now() + data.expires_in * 1000).toISOString();
      }

      persistAuthToStorage(get().user, get().subscriptions, payload.phone_number, expiresAtIso);

      set({
        otpPhoneNumber: payload.phone_number,
        otpExpiresAt: expiresAtIso,
        isLoading: false,
      });

      return true;
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'حدث خطأ أثناء إرسال رمز التحقق', isLoading: false });
      return false;
    }
  },

  verifyOtp: async (phoneNumber: string, code: string): Promise<boolean> => {
    set({ isLoading: true, error: null });

    try {
      const response = await fetch(`${API_BASE}/api/auth/otp/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ phone_number: phoneNumber, code }),
      });

      const data: OtpVerifyResponse = await response.json();

      if (!response.ok || !data.success || !data.user) {
        throw new Error(data.message || 'رمز التحقق غير صحيح');
      }

      const normalizedSubscriptions: Subscription[] = updateSubscriptionsInRealTime(
        data.subscriptions || [],
      );
      const normalizedUser: User = data.user;

      persistAuthToStorage(normalizedUser, normalizedSubscriptions, null, null);

      set({
        user: normalizedUser,
        subscriptions: normalizedSubscriptions,
        otpPhoneNumber: null,
        otpExpiresAt: null,
        isLoading: false,
      });

      return true;
    } catch (error) {
      set({ error: error instanceof Error ? error.message : 'حدث خطأ أثناء التحقق من الرمز', isLoading: false });
      return false;
    }
  },

  clearOtpState: () => {
    persistAuthToStorage(get().user, get().subscriptions, null, null);
    set({ otpPhoneNumber: null, otpExpiresAt: null });
  },

  setOtpState: (phoneNumber: string | null, expiresAt: string | null) => {
    persistAuthToStorage(get().user, get().subscriptions, phoneNumber, expiresAt);
    set({ otpPhoneNumber: phoneNumber, otpExpiresAt: expiresAt });
  },

  logout: () => {
    persistAuthToStorage(null, [], null, null);

    set({
      user: null,
      subscriptions: [],
      error: null,
      isLoading: false,
      otpPhoneNumber: null,
      otpExpiresAt: null,
    });
  },

  clearError: () => {
    set({ error: null });
  },

  updateSubscriptionsTimer: () => {
    const state = get();
    if (state.subscriptions.length > 0) {
      const updatedSubscriptions = updateSubscriptionsInRealTime(state.subscriptions);
      set({ subscriptions: updatedSubscriptions });

      try {
        persistAuthToStorage(state.user, updatedSubscriptions, state.otpPhoneNumber, state.otpExpiresAt);
      } catch (error) {
        console.error('خطأ في تحديث التخزين أثناء تحديث الاشتراكات:', error);
      }
    }
  },

  initializeFromStorage: () => {
    try {
      const savedUser = localStorage.getItem(USER_STORAGE_KEY);
      const savedSubscriptions = localStorage.getItem(SUBSCRIPTION_STORAGE_KEY);

      if (savedUser && savedSubscriptions) {
        const parsedSubscriptions: Subscription[] = JSON.parse(savedSubscriptions);
        const parsedUser: User = JSON.parse(savedUser);

        let restoredOtpPhone: string | null = null;
        let restoredOtpExpiry: string | null = null;
        const savedOtp = localStorage.getItem(OTP_STORAGE_KEY);

        if (savedOtp) {
          try {
            const parsedOtp = JSON.parse(savedOtp);
            if (parsedOtp?.expires_at && new Date(parsedOtp.expires_at).getTime() > Date.now()) {
              restoredOtpPhone = parsedOtp.phone_number ?? null;
              restoredOtpExpiry = new Date(parsedOtp.expires_at).toISOString();
            } else {
              localStorage.removeItem(OTP_STORAGE_KEY);
            }
          } catch (parseError) {
            localStorage.removeItem(OTP_STORAGE_KEY);
          }
        }

        set({
          user: parsedUser,
          subscriptions: updateSubscriptionsInRealTime(parsedSubscriptions),
          otpPhoneNumber: restoredOtpPhone,
          otpExpiresAt: restoredOtpExpiry,
        });
      }
    } catch (error) {
      console.error('خطأ في استرداد البيانات من localStorage:', error);
    }
  },
}));
